import React, { useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from '../ui/dropdown-menu';
import { LargeButton, SmallButton } from './RibbonPrimitives';
import { ObjectRenderer } from '../editor/ObjectRenderer';
import { SCHEME_PRESETS } from '../../lib/colors';
import { SHADOW_PRESETS, GLOW_SIZES, glowPreset, REFLECTION_PRESETS, SOFT_EDGE_SIZES, BEVEL_PRESETS, ROTATION3D_PRESETS, TEXT_SHADOW_PRESETS, TEXT_BEVEL_PRESETS, textEffectShadows, textReflectionCss } from '../../lib/effects';
import { Sparkles, Ban, SlidersHorizontal } from 'lucide-react';

const hover = 'hover:bg-[hsl(var(--wp-command-hover))] active:bg-[hsl(var(--wp-command-pressed))]';
const subContent = 'bg-popover text-popover-foreground border border-border shadow-md z-[90] p-2 max-h-[70vh] overflow-y-auto';
const subTrigger = 'text-[13px] px-2 py-1.5 gap-2';

/** Square preview tile used by every gallery. */
const Tile = ({ title, active, onClick, children, testId, size = 44 }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    onMouseDown={(e) => e.preventDefault()}
    className={`relative rounded-sm border flex items-center justify-center overflow-hidden transition-colors duration-150 ${active ? 'border-primary ring-2 ring-primary/30 bg-primary/10' : 'border-border bg-background hover:border-primary hover:bg-muted'}`}
    style={{ width: size, height: size }}
    data-testid={testId}
    data-state={active ? 'selected' : 'idle'}
  >
    {children}
  </button>
);

const GalleryLabel = ({ children }) => <div className="text-[11px] font-semibold text-muted-foreground mt-2 mb-1 px-0.5 first:mt-0">{children}</div>;
const Grid = ({ cols = 3, children }) => <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${cols}, 44px)` }}>{children}</div>;

/** Tiny shape sample rendered through the real renderer so the tile shows the true effect. */
const ShapeSample = ({ effect, value, color, size = 26, shapeType = 'rounded-rect' }) => {
  const obj = { id: 's', type: 'shape', shapeType, width: size, height: size, properties: { fill: { type: 'solid', color }, stroke: { width: 0 }, [effect]: value } };
  return <div style={{ width: size, height: size }}><ObjectRenderer object={obj} scale={1} /></div>;
};

const NoneTile = ({ label, active, onClick, testId }) => (
  <button type="button" onClick={onClick} onMouseDown={(e) => e.preventDefault()} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] mb-1 ${hover} ${active ? 'bg-primary/10' : ''}`} data-testid={testId}>
    <Ban className="h-4 w-4 text-muted-foreground" /> {label}
  </button>
);

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/* ------------------------------------------------------------------ */
/* Shape / Picture effects                                             */
/* ------------------------------------------------------------------ */

/**
 * Shape Effects (or Picture Effects) menu with Publisher-style variant galleries.
 * props: { p: current properties, set(patch), label, large, testId, disabled, sampleColor }
 */
export const ShapeEffectsMenu = ({ p = {}, set: setProp, label = 'Shape Effects', large = false, testId = 'ribbon-command-shape-effects', disabled, sampleColor, onOptions: onOptionsProp, tip = 'Shadow, reflection, glow, soft edges, bevel and 3-D rotation' }) => {
  const { state } = useEditor();
  const [open, setOpen] = useState(false);
  // Gallery tiles are plain buttons (not menu items) so they must close the menu themselves, like Publisher's galleries.
  const set = (patch) => { setProp(patch); setOpen(false); };
  const onOptions = onOptionsProp ? (section) => { setOpen(false); onOptionsProp(section); } : undefined;
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  const color = sampleColor || scheme[2]?.hex || '#4a6d8c';
  const glowColors = scheme.slice(1, 7).map((c) => c.hex);
  const prefix = testId.replace(/^ribbon-command-/, '');

  const shadowTiles = (list, sample = 'rounded-rect') => list.map((s) => (
    <Tile key={s.id} title={s.name} active={p.shadow?.preset === s.id} onClick={() => set({ shadow: { preset: s.id, ...s.value } })} testId={`${prefix}-shadow-${s.id}`}>
      <ShapeSample effect="shadow" value={s.value} color={color} shapeType={sample} size={s.value.cast ? 22 : 26} />
    </Tile>
  ));

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        {large ? <LargeButton icon={Sparkles} label={label} tip={tip} testId={testId} chevron disabled={disabled} /> : <SmallButton icon={Sparkles} label={label} tip={tip} testId={testId} chevron disabled={disabled} />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-popover text-popover-foreground border border-border shadow-md min-w-[200px] z-[80]" data-ribbon onCloseAutoFocus={(e) => e.preventDefault()}>
        {/* Shadow */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-shadow`}><ShapeSample effect="shadow" value={SHADOW_PRESETS.outer[0].value} color={color} size={16} /> Shadow</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Shadow" active={!p.shadow} onClick={() => set({ shadow: null })} testId={`${prefix}-shadow-none`} />
            <GalleryLabel>Outer</GalleryLabel><Grid>{shadowTiles(SHADOW_PRESETS.outer)}</Grid>
            <GalleryLabel>Inner</GalleryLabel><Grid>{shadowTiles(SHADOW_PRESETS.inner)}</Grid>
            <GalleryLabel>Perspective</GalleryLabel><Grid>{shadowTiles(SHADOW_PRESETS.perspective)}</Grid>
            {onOptions && <OptionsRow label="Shadow Options..." onClick={() => onOptions('shadow')} testId={`${prefix}-shadow-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Reflection */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-reflection`}><ShapeSample effect="reflection" value={REFLECTION_PRESETS[0].value} color={color} size={14} /> Reflection</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Reflection" active={!p.reflection} onClick={() => set({ reflection: null })} testId={`${prefix}-reflection-none`} />
            <GalleryLabel>Reflection Variations</GalleryLabel>
            <Grid>
              {REFLECTION_PRESETS.map((r) => (
                <Tile key={r.id} title={r.name} active={p.reflection?.preset === r.id} onClick={() => set({ reflection: r.value })} testId={`${prefix}-reflection-${r.id}`}>
                  <div style={{ marginTop: -14 }}><ShapeSample effect="reflection" value={r.value} color={color} size={20} /></div>
                </Tile>
              ))}
            </Grid>
            {onOptions && <OptionsRow label="Reflection Options..." onClick={() => onOptions('reflection')} testId={`${prefix}-reflection-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Glow */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-glow`}><ShapeSample effect="glow" value={glowPreset(5, glowColors[1])} color={color} size={16} /> Glow</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Glow" active={!p.glow} onClick={() => set({ glow: null })} testId={`${prefix}-glow-none`} />
            <GalleryLabel>Glow Variations</GalleryLabel>
            <Grid cols={6}>
              {GLOW_SIZES.flatMap((size) => glowColors.map((hex) => {
                const v = glowPreset(size, hex);
                const active = p.glow?.size === size && (p.glow?.color || '').toLowerCase() === hex.toLowerCase();
                return (
                  <Tile key={`${size}-${hex}`} title={`Glow: ${size} point, ${hex}`} active={active} onClick={() => set({ glow: v })} testId={`${prefix}-glow-${size}-${hex.replace('#', '')}`}>
                    <ShapeSample effect="glow" value={v} color={color} size={22} />
                  </Tile>
                );
              }))}
            </Grid>
            {onOptions && <OptionsRow label="Glow Options..." onClick={() => onOptions('glow')} testId={`${prefix}-glow-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Soft Edges */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-soft-edges`}><ShapeSample effect="softEdges" value={{ radius: 3 }} color={color} size={16} /> Soft Edges</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Soft Edges" active={!p.softEdges} onClick={() => set({ softEdges: null })} testId={`${prefix}-soft-edges-none`} />
            <GalleryLabel>Soft Edges Variations</GalleryLabel>
            <Grid cols={6}>
              {SOFT_EDGE_SIZES.map((r) => (
                <Tile key={r} title={`${r} Point`} active={p.softEdges?.radius === r} onClick={() => set({ softEdges: { radius: r } })} testId={`${prefix}-soft-edges-${String(r).replace('.', '_')}`}>
                  <ShapeSample effect="softEdges" value={{ radius: Math.min(r, 12) * 0.9 }} color={color} size={30} />
                </Tile>
              ))}
            </Grid>
            {onOptions && <OptionsRow label="Soft Edges Options..." onClick={() => onOptions('softEdges')} testId={`${prefix}-soft-edges-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* Bevel */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-bevel`}><ShapeSample effect="bevel" value={{ preset: 'circle' }} color={color} size={16} /> Bevel</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Bevel" active={!p.bevel} onClick={() => set({ bevel: null })} testId={`${prefix}-bevel-none`} />
            <GalleryLabel>Bevel</GalleryLabel>
            <Grid cols={4}>
              {BEVEL_PRESETS.map((b) => (
                <Tile key={b.id} title={b.name} active={p.bevel?.preset === b.id} onClick={() => set({ bevel: { preset: b.id, width: 1, depth: 1 } })} testId={`${prefix}-bevel-${b.id}`}>
                  <ShapeSample effect="bevel" value={{ preset: b.id, width: 0.7, depth: 1 }} color={color} size={32} />
                </Tile>
              ))}
            </Grid>
            {onOptions && <OptionsRow label="3-D Options..." onClick={() => onOptions('bevel')} testId={`${prefix}-bevel-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {/* 3-D Rotation */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-rotation3d`}><ShapeSample effect="rotation3d" value={ROTATION3D_PRESETS.parallel[0]} color={color} size={16} /> 3-D Rotation</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Rotation" active={!p.rotation3d} onClick={() => set({ rotation3d: null })} testId={`${prefix}-rotation3d-none`} />
            <GalleryLabel>Parallel</GalleryLabel>
            <Grid cols={5}>
              {ROTATION3D_PRESETS.parallel.map((r) => (
                <Tile key={r.id} title={r.name} active={p.rotation3d?.preset === r.id} onClick={() => set({ rotation3d: { preset: r.id, rx: r.rx, ry: r.ry, rz: r.rz, persp: r.persp } })} testId={`${prefix}-rotation3d-${r.id}`}>
                  <ShapeSample effect="rotation3d" value={r} color={color} size={26} shapeType="rectangle" />
                </Tile>
              ))}
            </Grid>
            <GalleryLabel>Perspective</GalleryLabel>
            <Grid cols={5}>
              {ROTATION3D_PRESETS.perspective.map((r) => (
                <Tile key={r.id} title={r.name} active={p.rotation3d?.preset === r.id} onClick={() => set({ rotation3d: { preset: r.id, rx: r.rx, ry: r.ry, rz: r.rz, persp: r.persp } })} testId={`${prefix}-rotation3d-${r.id}`}>
                  <ShapeSample effect="rotation3d" value={r} color={color} size={26} shapeType="rectangle" />
                </Tile>
              ))}
            </Grid>
            {onOptions && <OptionsRow label="3-D Rotation Options..." onClick={() => onOptions('rotation3d')} testId={`${prefix}-rotation3d-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => set({ shadow: null, reflection: null, glow: null, softEdges: null, bevel: null, rotation3d: null })} className="text-[13px]" data-testid={`${prefix}-clear`}>
          <Ban className="h-4 w-4 mr-2 text-muted-foreground" /> No Effects
        </DropdownMenuItem>
        {onOptions && <DropdownMenuItem onClick={() => onOptions()} className="text-[13px]" data-testid={`${prefix}-options`}><SlidersHorizontal className="h-4 w-4 mr-2 text-muted-foreground" /> Effects Options...</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const OptionsRow = ({ label, onClick, testId }) => (
  <>
    <DropdownMenuSeparator className="my-1.5" />
    <button type="button" onClick={onClick} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-[13px] ${hover}`} data-testid={testId}><SlidersHorizontal className="h-4 w-4 text-muted-foreground" /> {label}</button>
  </>
);

/* ------------------------------------------------------------------ */
/* Text (WordArt) effects                                              */
/* ------------------------------------------------------------------ */

const TextSample = ({ effects, color = '#1f2937', size = 26 }) => {
  const shadows = textEffectShadows(effects, 1);
  return (
    <span aria-hidden="true" className="font-outfit font-bold leading-none select-none" style={{ fontSize: size, color, textShadow: shadows.length ? shadows.join(', ') : undefined, WebkitBoxReflect: textReflectionCss(effects.reflection, 1) }}>A</span>
  );
};

/**
 * Text Effects menu (Text Box tab) with Shadow / Reflection / Glow / Bevel variant galleries.
 * props: { p: text properties, set(patch) }
 */
export const TextEffectsMenu = ({ p = {}, set, label = 'Effects', testId = 'ribbon-command-text-effects', disabled, onOptions: onOptionsProp }) => {
  const { state } = useEditor();
  const [open, setOpen] = useState(false);
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  const glowColors = scheme.slice(1, 7).map((c) => c.hex);
  const fx = p.textEffects || {};
  const setFx = (patch) => { set({ textEffects: { ...fx, ...patch } }); setOpen(false); };
  const onOptions = onOptionsProp ? (section) => { setOpen(false); onOptionsProp(section); } : undefined;
  const prefix = testId.replace(/^ribbon-command-/, '');
  const sampleColor = p.textFill?.type === 'solid' ? p.textFill.color : p.color || '#1f2937';

  const shadowTiles = (list) => list.map((s) => (
    <Tile key={s.id} title={s.name} active={fx.shadow?.preset === s.id} onClick={() => setFx({ shadow: s.value })} testId={`${prefix}-shadow-${s.id}`}>
      <TextSample effects={{ shadow: s.value }} color={sampleColor} />
    </Tile>
  ));

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <SmallButton icon={Sparkles} label={label} tip="Text effects: shadow, reflection, glow, bevel" testId={testId} chevron disabled={disabled} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="bg-popover text-popover-foreground border border-border shadow-md min-w-[190px] z-[80]" data-ribbon onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-shadow`}><TextSample effects={{ shadow: TEXT_SHADOW_PRESETS.outer[0].value }} size={15} /> Shadow</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Shadow" active={!fx.shadow} onClick={() => setFx({ shadow: null })} testId={`${prefix}-shadow-none`} />
            <GalleryLabel>Outer</GalleryLabel><Grid>{shadowTiles(TEXT_SHADOW_PRESETS.outer)}</Grid>
            <GalleryLabel>Perspective</GalleryLabel><Grid>{shadowTiles(TEXT_SHADOW_PRESETS.perspective)}</Grid>
            {onOptions && <OptionsRow label="Shadow Options..." onClick={() => onOptions('shadow')} testId={`${prefix}-shadow-options`} />}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-reflection`}><TextSample effects={{ reflection: { visible: 0.5, offset: 0, opacity: 0.4 } }} size={15} /> Reflection</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Reflection" active={!fx.reflection} onClick={() => setFx({ reflection: null })} testId={`${prefix}-reflection-none`} />
            <GalleryLabel>Reflection Variations</GalleryLabel>
            <Grid>
              {REFLECTION_PRESETS.map((r) => (
                <Tile key={r.id} title={r.name} active={fx.reflection?.preset === r.id} onClick={() => setFx({ reflection: { ...r.value, opacity: 0.4 } })} testId={`${prefix}-reflection-${r.id}`}>
                  <div style={{ marginTop: -12 }}><TextSample effects={{ reflection: { ...r.value, offset: Math.min(r.value.offset, 3) } }} color={sampleColor} size={20} /></div>
                </Tile>
              ))}
            </Grid>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-glow`}><TextSample effects={{ glow: { size: 4, color: glowColors[1], opacity: 0.8 } }} size={15} /> Glow</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Glow" active={!fx.glow} onClick={() => setFx({ glow: null })} testId={`${prefix}-glow-none`} />
            <GalleryLabel>Glow Variations</GalleryLabel>
            <Grid cols={6}>
              {GLOW_SIZES.flatMap((size) => glowColors.map((hex) => {
                const v = { preset: `glow-${size}`, size, color: hex, opacity: 0.75 };
                const active = fx.glow?.size === size && (fx.glow?.color || '').toLowerCase() === hex.toLowerCase();
                return (
                  <Tile key={`${size}-${hex}`} title={`Glow: ${size} point, ${hex}`} active={active} onClick={() => setFx({ glow: v })} testId={`${prefix}-glow-${size}-${hex.replace('#', '')}`}>
                    <TextSample effects={{ glow: { ...v, size: Math.min(size, 10) } }} color={sampleColor} size={24} />
                  </Tile>
                );
              }))}
            </Grid>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={subTrigger} data-testid={`${prefix}-bevel`}><TextSample effects={{ bevel: TEXT_BEVEL_PRESETS[0].value }} size={15} /> Bevel</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className={subContent} data-ribbon>
            <NoneTile label="No Bevel" active={!fx.bevel} onClick={() => setFx({ bevel: null })} testId={`${prefix}-bevel-none`} />
            <GalleryLabel>Bevel</GalleryLabel>
            <Grid cols={4}>
              {TEXT_BEVEL_PRESETS.map((b) => (
                <Tile key={b.id} title={b.name} active={fx.bevel?.preset === b.id} onClick={() => setFx({ bevel: b.value })} testId={`${prefix}-bevel-${b.id}`}>
                  <TextSample effects={{ bevel: b.value }} color={sampleColor} size={28} />
                </Tile>
              ))}
            </Grid>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => set({ textEffects: { shadow: null, glow: null, reflection: null, bevel: null }, textOutline: { color: '#000000', width: 0 }, textFill: null })} className="text-[13px]" data-testid={`${prefix}-clear`}>
          <Ban className="h-4 w-4 mr-2 text-muted-foreground" /> Clear Text Effects
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const effectsEqual = same;
export { Tile as GalleryTile, GalleryLabel, Grid as GalleryGrid, NoneTile as GalleryNoneTile };
